import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Tooltip,
  Badge,
  Popover,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CoffeeIcon from '@mui/icons-material/Coffee';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useCurrentSession, useClockAction } from '@/hooks';
import {
  useExplainShiftEndAutoClose,
  useMyShiftEndConfirmation,
  useRespondShiftEndConfirmation,
} from '@/hooks/useShiftEndConfirmation';
import { useNotify } from '@/stores/notificationStore';
import type { ClockStatus, TimeEntryType } from '@/types';
import { ShiftEndPromptDialog } from './ShiftEndPromptDialog';

const statusChipColor: Record<ClockStatus, 'success' | 'default' | 'warning'> = {
  clocked_in: 'success',
  clocked_out: 'default',
  on_break: 'warning',
  on_lunch: 'warning',
};

const badgeColor: Record<ClockStatus, 'success' | 'warning' | 'default'> = {
  clocked_in: 'success',
  clocked_out: 'default',
  on_break: 'warning',
  on_lunch: 'warning',
};

function formatDuration(minutes: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return t('durationHM', { hours: h, minutes: m });
}

function formatTime(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function ClockInButton() {
  const { t, i18n } = useTranslation('hr');
  const notify = useNotify();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const { data: session, isLoading } = useCurrentSession();
  const clockAction = useClockAction();
  const { data: shiftEndConfirmation } = useMyShiftEndConfirmation();
  const respondShiftEnd = useRespondShiftEndConfirmation();
  const explainAutoClose = useExplainShiftEndAutoClose();
  const [dismissedPrompt, setDismissedPrompt] = useState<{
    id: string;
    status: NonNullable<typeof shiftEndConfirmation>['status'];
  } | null>(null);
  const [reopenPrompt, setReopenPrompt] = useState(false);

  useEffect(() => {
    const open = () => setReopenPrompt(true);
    window.addEventListener('open-auto-clock-out', open);
    return () => window.removeEventListener('open-auto-clock-out', open);
  }, []);

  const status: ClockStatus = session?.status ?? 'clocked_out';

  // Live elapsed timer — only update state when the displayed minute changes
  const elapsedMinutesRef = useRef(0);
  useEffect(() => {
    if (!session?.currentEntryAt) {
      setElapsedSeconds(0);
      elapsedMinutesRef.current = 0;
      return;
    }
    const start = new Date(session.currentEntryAt).getTime();
    const tick = () => {
      const secs = Math.floor((Date.now() - start) / 1000);
      const mins = Math.floor(secs / 60);
      // Only trigger re-render when the displayed minute changes
      if (mins !== elapsedMinutesRef.current) {
        elapsedMinutesRef.current = mins;
        setElapsedSeconds(secs);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [session?.currentEntryAt]);

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);

  const handleAction = async (type: TimeEntryType) => {
    try {
      const result = await clockAction.mutateAsync(type);
      // A break pauses the job being timed and the break end resumes it (#3277).
      const paused = result.pausedWorkOrderIds?.length ?? 0;
      const resumed = result.resumedWorkOrderIds?.length ?? 0;
      if (paused > 0) notify.info(t('workPausedByClock', { count: paused }));
      if (resumed > 0) notify.info(t('workResumedByClock', { count: resumed }));
    } catch {
      notify.error(t('clockActionFailed'));
    }
  };

  const handleContinue = async (extensionMinutes: number, explanation: string) => {
    if (!shiftEndConfirmation) return;
    try {
      await respondShiftEnd.mutateAsync({
        id: shiftEndConfirmation.id,
        action: 'continue',
        extensionMinutes,
        explanation,
      });
    } catch {
      notify.error(t('shiftEndPrompt.respondFailed'));
    }
  };

  const handleStop = async () => {
    if (!shiftEndConfirmation) return;
    try {
      await respondShiftEnd.mutateAsync({
        id: shiftEndConfirmation.id,
        action: 'stop',
      });
    } catch {
      notify.error(t('shiftEndPrompt.respondFailed'));
    }
  };

  const handleExplain = async (explanation: string, clockOutAt?: string) => {
    if (!shiftEndConfirmation) return;
    try {
      await explainAutoClose.mutateAsync({
        id: shiftEndConfirmation.id,
        explanation,
        ...(clockOutAt ? { clockOutAt } : {}),
      });
    } catch {
      notify.error(t('shiftEndPrompt.respondFailed'));
    }
  };

  const showShiftEndPrompt =
    Boolean(shiftEndConfirmation) &&
    (reopenPrompt || dismissedPrompt?.id !== shiftEndConfirmation?.id ||
      dismissedPrompt?.status !== shiftEndConfirmation?.status);

  const actions = useMemo(() => {
    switch (status) {
      case 'clocked_out':
        return [
          { labelKey: 'clockIn', type: 'clock_in' as TimeEntryType, icon: <LoginIcon />, color: 'success' as const },
        ];
      case 'clocked_in':
        return [
          { labelKey: 'goOnBreak', type: 'break_start' as TimeEntryType, icon: <CoffeeIcon />, color: 'warning' as const },
          { labelKey: 'goOnLunch', type: 'lunch_start' as TimeEntryType, icon: <RestaurantIcon />, color: 'warning' as const },
          { labelKey: 'clockOut', type: 'clock_out' as TimeEntryType, icon: <LogoutIcon />, color: 'error' as const },
        ];
      case 'on_break':
        return [
          { labelKey: 'returnToWork', type: 'break_end' as TimeEntryType, icon: <PlayArrowIcon />, color: 'success' as const },
        ];
      case 'on_lunch':
        return [
          { labelKey: 'returnToWork', type: 'lunch_end' as TimeEntryType, icon: <PlayArrowIcon />, color: 'success' as const },
        ];
    }
  }, [status]);

  return (
    <>
      <Tooltip title={t('timeTracking')}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ ml: 1 }}
          aria-label={t('timeTracking')}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
        >
          <Badge
            variant="dot"
            color={badgeColor[status]}
            invisible={status === 'clocked_out'}
          >
            <AccessTimeIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'calc(100vw - 32px)', sm: 300 },
              mt: 1,
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Current Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('currentStatus')}
            </Typography>
            <Chip
              label={t(`clockStatus.${status}`)}
              color={statusChipColor[status]}
              size="small"
            />
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* Clock-in time */}
              {session?.clockInAt && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('clockedInAt')}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatTime(session.clockInAt, i18n.language)}
                  </Typography>
                </Box>
              )}

              {/* Break/lunch elapsed */}
              {(status === 'on_break' || status === 'on_lunch') && session?.currentEntryAt && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {status === 'on_break' ? t('breakStarted') : t('lunchStarted')}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatTime(session.currentEntryAt, i18n.language)} ({formatDuration(elapsedMinutes, t)})
                  </Typography>
                </Box>
              )}

              {/* Today stats */}
              {status !== 'clocked_out' && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('todayWorked')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDuration(session?.todayTotalMinutes ?? 0, t)}
                    </Typography>
                  </Box>
                  {(session?.todayBreakMinutes ?? 0) > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('todayBreaks')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDuration(session?.todayBreakMinutes ?? 0, t)}
                      </Typography>
                    </Box>
                  )}
                  {(session?.todayLunchMinutes ?? 0) > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('todayLunch')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDuration(session?.todayLunchMinutes ?? 0, t)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Action buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {actions.map((action) => (
                  <Button
                    key={action.type}
                    variant={action.color === 'success' ? 'contained' : 'outlined'}
                    color={action.color}
                    startIcon={action.icon}
                    onClick={() => void handleAction(action.type)}
                    disabled={clockAction.isPending}
                    fullWidth
                  >
                    {t(action.labelKey)}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Popover>

      {showShiftEndPrompt && shiftEndConfirmation && (
        <ShiftEndPromptDialog
          key={`${shiftEndConfirmation.id}:${shiftEndConfirmation.status}`}
          confirmation={shiftEndConfirmation}
          locale={i18n.language}
          isPending={respondShiftEnd.isPending || explainAutoClose.isPending}
          onContinue={(extensionMinutes, explanation) => {
            void handleContinue(extensionMinutes, explanation);
          }}
          onStop={() => {
            void handleStop();
          }}
          onExplain={(explanation, clockOutAt) => {
            void handleExplain(explanation, clockOutAt);
          }}
          onDismiss={() => {
            setReopenPrompt(false);
            setDismissedPrompt({
              id: shiftEndConfirmation.id,
              status: shiftEndConfirmation.status,
            });
          }}
        />
      )}
    </>
  );
}
